import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import {
  useCreateAgentDefinitionDraft,
  useDeleteAgentDefinitionDraft,
  useFinalizeAgentDefinitionVersion,
  useAgentDefinitionVersions,
  useGetAgentDefinition,
  useLoadAgentDefinitionVersion,
  usePublishAgentDefinitionVersion,
  useUpdateAgentDefinition,
  useUpdateAgentDefinitionDraft,
} from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { useToast } from 'src/context/ToastContext';
import { AuthContext } from 'src/context/auth.context';
import { useGT } from 'gt-react';
import {
  deriveDirtyState,
  initAgentForm,
  mergeConfigForSave,
  type AgentFormFields,
} from './agentDefinitionFormState';
import type {
  AgentDefinitionResponse,
  AgentDefinitionStaleBaseWarning,
  AgentDefinitionVersionDetail,
  UpdateAgentDefinitionDraftRequest,
} from '@erp/rpc';
import { AgentDraftConflictDialog } from './AgentDraftConflictDialog';
import {
  deletedAtOf,
  forkSource,
  isRecordPresent,
  editorViewState,
  errorMessage,
  pinnedVersionIdOf,
  resolveCurrentVersion,
  versionHistoryReady,
  snapshotVersion,
  startAgentEdit,
  userIdOf,
  versionsOf,
  type DraftConflictPlan,
  type DraftNotice,
  type EditPlan,
} from './agentDefinitionEditActions';

export type { AgentFormFields };

/**
 * What finalizing a draft did, as the callers need to distinguish it:
 * - `finalized` — the version is finalized (and published, if asked).
 * - `stale_base` — refused; the draft's base is no longer the published version.
 * - `aborted` — nothing was finalized because the pre-save failed or the version is not editable.
 *   `save()` has already surfaced the reason, so callers stay silent and leave their dialog open.
 */
export type FinalizeDraftOutcome =
  | { status: 'finalized' }
  | { status: 'stale_base'; warning: AgentDefinitionStaleBaseWarning }
  | { status: 'aborted' };

export type AgentDefinitionFormContextValue = {
  definition: AgentDefinitionResponse | undefined;
  currentVersion: AgentDefinitionVersionDetail | null;
  isLoading: boolean;
  error: Error | null;
  isArchived: boolean;
  isEditable: boolean;
  isVersionLoading: boolean;

  agentForm: AgentFormFields | null;
  setAgentForm: React.Dispatch<React.SetStateAction<AgentFormFields | null>>;

  enabledTools: string[];
  baseTools: AgentDefinitionResponse['tools'];
  setLocalTools: React.Dispatch<React.SetStateAction<string[] | null>>;
  localConfig: AgentDefinitionResponse['config'] | null;
  baseConfig: AgentDefinitionResponse['config'];
  setLocalConfig: React.Dispatch<
    React.SetStateAction<AgentDefinitionResponse['config'] | null>
  >;

  isAgentDirty: boolean;
  isToolsDirty: boolean;
  isConfigDirty: boolean;
  isDirty: boolean;

  beginEdit: () => Promise<void>;
  editPlan: EditPlan;
  draftNotice: DraftNotice;
  draftConflict: DraftConflictPlan | null;
  dismissDraftConflict: () => void;
  resumeDraftConflict: () => Promise<void>;
  replaceDraftConflict: () => Promise<void>;
  viewVersion: (versionId: string) => Promise<void>;
  save: () => Promise<boolean>;
  finalizeDraft: (
    publish: boolean,
    acknowledgeStaleBase?: boolean,
  ) => Promise<FinalizeDraftOutcome>;
  publishVersion: (
    versionId: string,
    acknowledgeStaleBase?: boolean,
  ) => Promise<AgentDefinitionStaleBaseWarning | null>;
  discardDraft: (versionId: string) => Promise<void>;
  isSaving: boolean;
  isDraftActionPending: boolean;
};

function publishedVersionOf(
  definition: AgentDefinitionResponse,
): AgentDefinitionVersionDetail | null {
  if (!definition.pinnedVersionId) return null;
  return {
    id: definition.pinnedVersionId,
    version: definition.version,
    status: 'finalized',
    parentVersionId: null,
    changelog: null,
    isPinned: true,
    createdBy: definition.createdBy,
    createdByName: null,
    createdAt: definition.updatedAt,
    systemPrompt: definition.systemPrompt,
    modelConfig: definition.modelConfig,
    config: definition.config,
    inputFields: definition.inputFields,
    outputFields: definition.outputFields,
    tools: definition.tools,
    configWarnings: definition.configWarnings,
  };
}

const AgentDefinitionFormContext =
  createContext<AgentDefinitionFormContextValue | null>(null);

export function useAgentDefinitionForm() {
  const ctx = useContext(AgentDefinitionFormContext);
  if (!ctx) {
    throw new Error(
      'useAgentDefinitionForm must be used inside <AgentDefinitionFormProvider>',
    );
  }
  return ctx;
}

export function AgentDefinitionFormProvider({
  agentDefinitionId,
  onSaved,
  children,
}: {
  agentDefinitionId: string;
  onSaved?: () => void;
  children: ReactNode;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const { currentUser } = useContext(AuthContext);
  const updateAgent = useUpdateAgentDefinition();
  const createDraft = useCreateAgentDefinitionDraft();
  const updateDraft = useUpdateAgentDefinitionDraft();
  const finalizeVersion = useFinalizeAgentDefinitionVersion();
  const publishVersionMutation = usePublishAgentDefinitionVersion();
  const deleteDraft = useDeleteAgentDefinitionDraft();
  const loadVersion = useLoadAgentDefinitionVersion();

  const {
    data: definition,
    isLoading,
    error,
  } = useGetAgentDefinition(agentDefinitionId);
  const {
    data: versionList,
    isSuccess: versionsSucceeded,
    isError: versionsFailed,
    refetch: refetchVersions,
  } = useAgentDefinitionVersions(agentDefinitionId);

  const [selectedVersion, setSelectedVersion] =
    useState<AgentDefinitionVersionDetail | null>(null);
  const [draftConflict, setDraftConflict] = useState<DraftConflictPlan | null>(
    null,
  );
  const [agentForm, setAgentForm] = useState<AgentFormFields | null>(null);
  const [localTools, setLocalTools] = useState<string[] | null>(null);
  const [localConfig, setLocalConfig] = useState<
    AgentDefinitionResponse['config'] | null
  >(null);
  const seededVersionRef = useRef<string | null>(null);

  const publishedVersion = useMemo(() => {
    if (!definition) return null;
    return publishedVersionOf(definition);
  }, [definition]);
  const currentVersion = resolveCurrentVersion(
    selectedVersion,
    publishedVersion,
  );

  useEffect(() => {
    if (!definition || !currentVersion) return;
    if (seededVersionRef.current === currentVersion.id) return;

    seededVersionRef.current = currentVersion.id;
    setAgentForm(initAgentForm(definition, currentVersion));
    setLocalTools(null);
    setLocalConfig(null);
  }, [definition, currentVersion]);

  const baseTools = useMemo(
    () => currentVersion?.tools ?? definition?.tools ?? [],
    [currentVersion?.tools, definition?.tools],
  );
  const originalTools = useMemo(
    () => baseTools.map((tool) => tool.toolType),
    [baseTools],
  );
  const enabledTools = useMemo(
    () => (localTools !== null ? localTools : originalTools),
    [localTools, originalTools],
  );
  const baseConfig = useMemo(
    () => currentVersion?.config ?? definition?.config ?? {},
    [currentVersion?.config, definition?.config],
  );

  const {
    isMetadataDirty,
    isPayloadDirty,
    isAgentDirty,
    isToolsDirty,
    isConfigDirty,
    isDirty,
  } = deriveDirtyState({
    baselineForm: dirtyBaseline(definition, currentVersion),
    agentForm,
    localTools,
    enabledTools,
    originalTools,
    localConfig,
    baseConfig,
  });

  const userId = userIdOf(currentUser);
  const viewedSnapshot = snapshotVersion(currentVersion);
  const { isArchived, isEditable, editPlan, draftNotice } = editorViewState({
    deletedAt: deletedAtOf(definition),
    viewed: viewedSnapshot,
    versions: versionsOf(versionList),
    versionsLoaded: versionHistoryReady(versionsSucceeded, versionsFailed),
    userId,
    publishedVersionId: pinnedVersionIdOf(definition),
  });

  const viewVersion = useCallback(
    async (versionId: string) => {
      if (!definition || versionId === currentVersion?.id || isDirty) return;
      if (versionId === definition.pinnedVersionId) {
        seededVersionRef.current = null;
        setSelectedVersion(null);
        return;
      }
      try {
        const version = await loadVersion.mutateAsync({
          agentDefinitionId,
          versionId,
        });
        seededVersionRef.current = null;
        setSelectedVersion(version);
      } catch (loadError) {
        showToast({
          title: gt('Could not load version', {
            context: 'agent version loading error toast',
          }),
          message:
            loadError instanceof Error ? loadError.message : String(loadError),
          color: 'error',
        });
      }
    },
    [
      agentDefinitionId,
      currentVersion?.id,
      definition,
      gt,
      isDirty,
      loadVersion,
      showToast,
    ],
  );

  const forkFromViewed = useCallback(async () => {
    const source = forkSource(definition, currentVersion, isArchived);
    if (!source) return;
    try {
      const result = await createDraft.mutateAsync({
        agentDefinitionId,
        sourceVersionId: source.id,
      });
      seededVersionRef.current = null;
      setSelectedVersion(result.version);
      showToast(
        forkResultToast(
          gt,
          result.resumed,
          result.version.version,
          source.version,
        ),
      );
    } catch (draftError) {
      showToast({
        title: gt('Could not create draft', {
          context: 'agent version editing error toast',
        }),
        message: errorMessage(draftError),
        color: 'error',
      });
    }
  }, [
    agentDefinitionId,
    createDraft,
    currentVersion,
    definition,
    gt,
    isArchived,
    showToast,
  ]);

  const loadVersionsForEdit = useCallback(async () => {
    const loaded = versionsOf(versionList);
    if (loaded) return loaded;
    const result = await refetchVersions();
    return versionsOf(result.data) ?? null;
  }, [refetchVersions, versionList]);

  const beginEdit = useCallback(
    () =>
      startAgentEdit({
        archived: isArchived,
        hasDefinition: isRecordPresent(definition),
        viewed: viewedSnapshot,
        userId,
        loadVersions: loadVersionsForEdit,
        openConflict: setDraftConflict,
        fork: forkFromViewed,
        onVersionsUnavailable: () => {
          showToast({
            title: gt('Could not check for a draft', {
              context: 'toast when agent version history fails to load',
            }),
            message: gt('Version history did not load. Try Edit again.', {
              context: 'toast when agent version history fails to load',
            }),
            color: 'error',
          });
        },
      }),
    [
      definition,
      forkFromViewed,
      gt,
      isArchived,
      loadVersionsForEdit,
      showToast,
      userId,
      viewedSnapshot,
    ],
  );

  const dismissDraftConflict = useCallback(() => {
    setDraftConflict(null);
  }, []);

  const resumeDraftConflict = useCallback(async () => {
    if (!draftConflict) return;
    const versionId = draftConflict.draftId;
    setDraftConflict(null);
    await viewVersion(versionId);
  }, [draftConflict, viewVersion]);

  const replaceDraftConflict = useCallback(async () => {
    if (!draftConflict) return;
    try {
      await deleteDraft.mutateAsync({
        agentDefinitionId,
        versionId: draftConflict.draftId,
      });
      setDraftConflict(null);
      await forkFromViewed();
    } catch (replaceError) {
      showToast({
        title: gt('Could not replace draft', {
          context: 'toast when replacing an agent draft fails',
        }),
        message: errorMessage(replaceError),
        color: 'error',
      });
    }
  }, [
    agentDefinitionId,
    deleteDraft,
    draftConflict,
    forkFromViewed,
    gt,
    showToast,
  ]);

  const save = useCallback(async (): Promise<boolean> => {
    if (
      !definition ||
      !currentVersion ||
      !agentForm ||
      !isDirty ||
      !isEditable
    ) {
      return !isDirty;
    }

    // Metadata and payload are two separate writes. Run them in sequence rather than
    // concurrently so a failure has an unambiguous boundary: if the payload write fails, the
    // caller knows the metadata went through and can say so.
    let metadataSaved = false;
    try {
      if (isMetadataDirty) {
        await updateAgent.mutateAsync({
          agentDefinitionId,
          name: agentForm.name,
          description: agentForm.description || null,
        });
        metadataSaved = true;
      }

      let updatedVersion: AgentDefinitionVersionDetail | null = null;
      if (isPayloadDirty || isToolsDirty || isConfigDirty) {
        const payload: UpdateAgentDefinitionDraftRequest = {
          agentDefinitionId,
          versionId: currentVersion.id,
          systemPrompt: agentForm.systemPrompt,
          modelConfig: agentForm.modelConfig,
          config: mergeConfigForSave(agentForm.config, localConfig),
          inputFields: agentForm.inputFields,
          outputFields: agentForm.outputFields,
          tools: enabledTools.map((toolType) => ({ toolType })),
        };
        updatedVersion = await updateDraft.mutateAsync(payload);
      }

      if (updatedVersion) setSelectedVersion(updatedVersion);
      setAgentForm({
        ...agentForm,
        config: mergeConfigForSave(agentForm.config, localConfig),
      });
      setLocalTools(null);
      setLocalConfig(null);
      showToast({
        title: gt('Draft saved', { context: 'toast after saving agent draft' }),
        message: gt('Published agent runs are unchanged.', {
          context: 'toast message after saving agent draft',
        }),
        color: 'success',
      });
      onSaved?.();
      return true;
    } catch (saveError) {
      const reason =
        saveError instanceof Error ? saveError.message : String(saveError);
      showToast({
        title: gt('Save failed', { context: 'toast title' }),
        // Name and description save separately from the behavior payload, so say which half
        // landed instead of letting the user guess what to redo.
        message: metadataSaved
          ? gt(
              'The name and description were saved, but the behavior changes were not: {reason}',
              {
                context: 'toast when an agent draft partially fails to save',
                reason,
              },
            )
          : reason,
        color: 'error',
      });
      return false;
    }
  }, [
    agentDefinitionId,
    agentForm,
    currentVersion,
    definition,
    enabledTools,
    gt,
    isConfigDirty,
    isDirty,
    isEditable,
    isToolsDirty,
    localConfig,
    isMetadataDirty,
    onSaved,
    isPayloadDirty,
    showToast,
    updateAgent,
    updateDraft,
  ]);

  const finalizeDraft = useCallback(
    async (
      publish: boolean,
      acknowledgeStaleBase = false,
    ): Promise<FinalizeDraftOutcome> => {
      // `aborted` exists so a failed pre-save cannot read as success: `save()` has already told
      // the user what went wrong, and the draft is still an unfinalized draft.
      if (!currentVersion || !isEditable) return { status: 'aborted' };
      if (isDirty && !(await save())) return { status: 'aborted' };

      const response = await finalizeVersion.mutateAsync({
        agentDefinitionId,
        versionId: currentVersion.id,
        publish,
        acknowledgeStaleBase,
      });
      if (!response.ok) {
        return { status: 'stale_base', warning: response.warning };
      }

      seededVersionRef.current = null;
      setSelectedVersion(publish ? null : response.version);
      setAgentForm(initAgentForm(response.definition, response.version));
      setLocalTools(null);
      setLocalConfig(null);
      return { status: 'finalized' };
    },
    [
      agentDefinitionId,
      currentVersion,
      finalizeVersion,
      isDirty,
      isEditable,
      save,
    ],
  );

  const publishVersion = useCallback(
    async (versionId: string, acknowledgeStaleBase = false) => {
      const response = await publishVersionMutation.mutateAsync({
        agentDefinitionId,
        versionId,
        acknowledgeStaleBase,
      });
      if (!response.ok) return response.warning;
      seededVersionRef.current = null;
      setSelectedVersion(null);
      return null;
    },
    [agentDefinitionId, publishVersionMutation],
  );

  const discardDraft = useCallback(
    async (versionId: string) => {
      await deleteDraft.mutateAsync({ agentDefinitionId, versionId });
      if (currentVersion?.id === versionId) {
        seededVersionRef.current = null;
        setSelectedVersion(null);
      }
    },
    [agentDefinitionId, currentVersion?.id, deleteDraft],
  );

  const isSaving = savePending(updateAgent.isPending, updateDraft.isPending);
  const isDraftActionPending = draftMutationPending({
    creating: createDraft.isPending,
    finalizing: finalizeVersion.isPending,
    publishing: publishVersionMutation.isPending,
    deleting: deleteDraft.isPending,
  });

  const value = useMemo<AgentDefinitionFormContextValue>(
    () => ({
      definition,
      currentVersion,
      isLoading,
      error: error instanceof Error ? error : null,
      isArchived,
      isEditable,
      isVersionLoading: loadVersion.isPending,
      agentForm,
      setAgentForm,
      enabledTools,
      baseTools,
      setLocalTools,
      localConfig,
      baseConfig,
      setLocalConfig,
      isAgentDirty,
      isToolsDirty,
      isConfigDirty,
      isDirty,
      beginEdit,
      editPlan,
      draftNotice,
      draftConflict,
      dismissDraftConflict,
      resumeDraftConflict,
      replaceDraftConflict,
      viewVersion,
      save,
      finalizeDraft,
      publishVersion,
      discardDraft,
      isSaving,
      isDraftActionPending,
    }),
    [
      agentForm,
      baseConfig,
      baseTools,
      beginEdit,
      currentVersion,
      definition,
      dismissDraftConflict,
      draftConflict,
      draftNotice,
      editPlan,
      discardDraft,
      enabledTools,
      error,
      finalizeDraft,
      isAgentDirty,
      isArchived,
      isConfigDirty,
      isDirty,
      isDraftActionPending,
      isEditable,
      isLoading,
      isSaving,
      isToolsDirty,
      loadVersion.isPending,
      localConfig,
      publishVersion,
      replaceDraftConflict,
      resumeDraftConflict,
      save,
      viewVersion,
    ],
  );

  return (
    <AgentDefinitionFormContext.Provider value={value}>
      {children}
      <AgentDraftConflictDialog
        plan={draftConflict}
        pending={isDraftActionPending}
        onDismiss={dismissDraftConflict}
        onResume={() => void resumeDraftConflict()}
        onReplace={() => void replaceDraftConflict()}
      />
    </AgentDefinitionFormContext.Provider>
  );
}

function dirtyBaseline(
  definition: AgentDefinitionResponse | undefined,
  version: AgentDefinitionVersionDetail | null,
): AgentFormFields | null {
  if (!definition) return null;
  if (!version) return null;
  return initAgentForm(definition, version);
}

function savePending(updatingAgent: boolean, updatingDraft: boolean): boolean {
  if (updatingAgent) return true;
  return updatingDraft;
}

function draftMutationPending(flags: {
  creating: boolean;
  finalizing: boolean;
  publishing: boolean;
  deleting: boolean;
}): boolean {
  if (flags.creating) return true;
  if (flags.finalizing) return true;
  if (flags.publishing) return true;
  return flags.deleting;
}

function forkResultToast(
  gt: ReturnType<typeof useGT>,
  resumed: boolean,
  version: number,
  sourceVersion: number,
): { title: string; message: string; color: 'success' | 'warning' } {
  if (resumed) {
    return {
      title: gt('Opened your existing draft', {
        context: 'toast when edit resumes an agent draft that already existed',
      }),
      message: gt(
        'Draft v{version} was already open, so this did not copy the version you were viewing. Discard that draft if you want to start over from the version on screen.',
        {
          context:
            'toast when edit resumes an agent draft that already existed',
          version: String(version),
        },
      ),
      color: 'warning',
    };
  }
  return {
    title: gt('Draft created', { context: 'agent version editing toast' }),
    message: gt(
      'Draft v{version} is a copy of v{source}. Published runs stay on the current version until you publish.',
      {
        context: 'agent version editing toast message',
        version: String(version),
        source: String(sourceVersion),
      },
    ),
    color: 'success',
  };
}
